/**
 * TimezoneWidget Component
 *
 * Displays timezone information including:
 * - Local user time (browser timezone)
 * - Factory time (configured factory timezone)
 * - UTC time
 *
 * Integrates with i18next for internationalization
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFactoryTimezone } from '@/hooks/useFactoryTimezone';
import { useCurrentLanguage } from '@/hooks/useUserLanguage';
import { formatTimeOnly, formatDateOnly, getUserDateFormatLocale } from '@/utils/timezone';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

// Component props
interface TimezoneWidgetProps {
  className?: string;
}

// Time display item interface
interface TimeDisplay {
  label: string;
  time: string;
  date: string;
  timezone: string;
  highlight?: boolean;
}

/**
 * TimezoneWidget Component
 *
 * Displays current factory time and timezone information
 * Updates every second for real-time accuracy
 *
 * @param className - Optional CSS class name
 */
export function TimezoneWidget({ className }: TimezoneWidgetProps): React.JSX.Element {
  const { t } = useTranslation();
  const { timezone: factoryTimezone, isLoading } = useFactoryTimezone();
  const { currentLanguage } = useCurrentLanguage();
  const userLocale = getUserDateFormatLocale(currentLanguage);

  // State for current time
  const [currentTime, setCurrentTime] = useState<Date>(new Date());

  useEffect(() => {
    const interval = setInterval((): void => {
      setCurrentTime(new Date());
    }, 1000);

    return (): void => clearInterval(interval);
  }, []);

  const getUserTimezone = (): string => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  };

  const formatTimeForTimezone = (date: Date, timezone: string): string => {
    return formatTimeOnly(date.toISOString(), timezone, userLocale);
  };

  const formatDateForTimezone = (date: Date, timezone: string): string => {
    return formatDateOnly(date.toISOString(), timezone, userLocale);
  };

  const getUTCOffset = (timezone: string): string => {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'shortOffset',
      });
      const parts = formatter.formatToParts(currentTime);
      const offsetPart = parts.find(part => part.type === 'timeZoneName');
      let offset = offsetPart?.value || '';
      offset = offset.replace('GMT', 'UTC');

      if (offset === 'UTC') {
        return 'UTC+0';
      }

      return offset;
    } catch {
      return '';
    }
  };

  // Prepare timezone data for display
  const localTimezone = getUserTimezone();

  const timeDisplays: TimeDisplay[] = [
    // Local user timezone
    {
      label: t('timezone.local_time'),
      time: formatTimeForTimezone(currentTime, localTimezone),
      date: formatDateForTimezone(currentTime, localTimezone),
      timezone: localTimezone,
      highlight: localTimezone === factoryTimezone,
    },
    // Factory timezone
    {
      label: t('timezone.factory_time'),
      time: formatTimeForTimezone(currentTime, factoryTimezone),
      date: formatDateForTimezone(currentTime, factoryTimezone),
      timezone: factoryTimezone,
      highlight: true,
    },
    // UTC reference time
    {
      label: t('timezone.utc_time'),
      time: formatTimeForTimezone(currentTime, 'UTC'),
      date: formatDateForTimezone(currentTime, 'UTC'),
      timezone: 'UTC',
      highlight: false,
    },
  ];

  if (isLoading) {
    return (
      <Button variant="outline" size="icon" className={className} disabled>
        <span className="text-xs">⏳</span>
        <span className="sr-only">{t('common.loading')}</span>
      </Button>
    );
  }

  const factoryTime = formatTimeForTimezone(currentTime, factoryTimezone);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className={className}>
          <div className="flex items-center gap-2">
            <svg
              className="h-5 w-5 text-muted-foreground"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span className="text-base font-medium">{factoryTime}</span>
          </div>
          <span className="sr-only">{t('timezone.timezone_comparison')}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="px-3 py-2">
          {/* Header */}
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            {t('timezone.timezone_comparison')}
          </div>
          {/* Timezone displays */}
          {timeDisplays.map((display, index) => (
            <div key={`${display.label}-${display.timezone}-${index}`}>
              <div
                className={`py-3 ${display.highlight ? 'bg-accent/50 -mx-3 px-3 rounded-md' : ''}`}
              >
                {/* Label and primary badge */}
                <div className="flex items-start justify-between mb-1">
                  <span className="text-xs font-medium text-muted-foreground">{display.label}</span>
                </div>

                {/* Time display */}
                <div className="text-xl font-bold tracking-tight">{display.time}</div>

                {/* Date display */}
                <div className="text-sm text-muted-foreground mt-0.5">{display.date}</div>

                {/* Timezone info */}
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs text-muted-foreground">
                    {getUTCOffset(display.timezone)}
                  </span>
                </div>
              </div>

              {/* Separator between items */}
              {index < timeDisplays.length - 1 && <DropdownMenuSeparator className="my-0" />}
            </div>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
